// src/contexts/BackgroundGenerationContext.tsx
// Context for managing background proposal generations with cancellation support

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Contract } from '../types';
import { supabase } from '../lib/supabase';
import {
  generateSourceGroundedProposal,
  GenerationProgress,
  SourceGroundedGenerationRequest,
} from '../services/source-grounded-proposal-service';
import { SourceGroundedProposalOutput } from '../types/source-grounded';
import { normalizeForStorage } from '../lib/markdown-utils';
import toast from 'react-hot-toast';

// ============================================================
// TYPES
// ============================================================

export interface GenerationTask {
  id: string;
  contractId: string;
  contractTitle: string;
  status: 'pending' | 'analyzing' | 'generating' | 'complete' | 'error' | 'cancelled';
  progress: number;
  currentStep: string;
  stepDetail?: string;
  startedAt: Date;
  completedAt?: Date;
  error?: string;
  output?: SourceGroundedProposalOutput;
  // Metrics
  evidenceCount?: number;
  requirementCount?: number;
  matchedCount?: number;
}

interface BackgroundGenerationContextType {
  tasks: GenerationTask[];
  isWidgetExpanded: boolean;
  isWidgetMinimized: boolean;
  startGeneration: (contract: Contract, profileId: string) => string;
  cancelGeneration: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  clearCompleted: () => void;
  toggleWidgetExpanded: () => void;
  toggleWidgetMinimized: () => void;
  getTask: (taskId: string) => GenerationTask | undefined;
}

// ============================================================
// CONTEXT
// ============================================================

const BackgroundGenerationContext = createContext<BackgroundGenerationContextType | undefined>(undefined);

export function useBackgroundGeneration() {
  const context = useContext(BackgroundGenerationContext);
  if (!context) {
    throw new Error('useBackgroundGeneration must be used within a BackgroundGenerationProvider');
  }
  return context;
}

// ============================================================
// PROVIDER
// ============================================================

export function BackgroundGenerationProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<GenerationTask[]>([]);
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(true);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);

  // AbortControllers for each task
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  // Update a task
  const updateTask = useCallback((taskId: string, updates: Partial<GenerationTask>) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, ...updates } : task
    ));
  }, []);

  // Start a new generation
  const startGeneration = useCallback((contract: Contract, profileId: string): string => {
    const taskId = `gen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const abortController = new AbortController();
    abortControllers.current.set(taskId, abortController);

    const newTask: GenerationTask = {
      id: taskId,
      contractId: contract.id || contract.notice_id || '',
      contractTitle: contract.title,
      status: 'analyzing',
      progress: 0,
      currentStep: 'Starting analysis...',
      startedAt: new Date(),
    };

    setTasks(prev => [newTask, ...prev]);
    setIsWidgetMinimized(false); // Show the widget

    // Run generation in background
    runGeneration(taskId, contract, profileId, abortController.signal);

    return taskId;
  }, []);

  // Run the actual generation
  const runGeneration = async (
    taskId: string,
    contract: Contract,
    profileId: string,
    signal: AbortSignal
  ) => {
    try {
      // Check for cancellation
      if (signal.aborted) {
        updateTask(taskId, { status: 'cancelled', currentStep: 'Cancelled' });
        return;
      }

      // Analyze RFP first
      updateTask(taskId, {
        status: 'analyzing',
        currentStep: 'Analyzing RFP...',
        progress: 5
      });

      const { data: rfpData, error: rfpError } = await supabase.functions.invoke('analyze-rfp', {
        body: JSON.stringify({ contract }),
      });

      if (signal.aborted) {
        updateTask(taskId, { status: 'cancelled', currentStep: 'Cancelled' });
        return;
      }

      if (rfpError) console.warn('RFP analysis warning:', rfpError);

      // Build request
      const request: SourceGroundedGenerationRequest = {
        profileId,
        contractId: contract.id || contract.notice_id || '',
        contract: {
          id: contract.id || contract.notice_id || '',
          title: contract.title,
          agency: contract.agency,
          naicsCode: contract.naicsCode,
          setAsides: contract.setAsides,
          fundingAmount: contract.fundingAmount,
          description: contract.description,
          solicitationNumber: contract.solicitationNumber,
          dueDate: contract.dueDate,
        },
        rfpAnalysis: rfpData?.analysis || rfpData,
      };

      updateTask(taskId, { status: 'generating', currentStep: 'Starting generation...' });

      // Progress handler
      const handleProgress = (prog: GenerationProgress) => {
        if (signal.aborted) return;

        updateTask(taskId, {
          progress: prog.progress,
          currentStep: prog.message,
          stepDetail: prog.details?.currentSection,
          evidenceCount: prog.details?.evidenceCount,
          requirementCount: prog.details?.requirementCount,
          matchedCount: prog.details?.matchedCount,
        });
      };

      // Generate proposal
      const result = await generateSourceGroundedProposal(request, handleProgress);

      if (signal.aborted) {
        updateTask(taskId, { status: 'cancelled', currentStep: 'Cancelled' });
        return;
      }

      // Store the proposal
      await storeProposal(contract, result, profileId);

      updateTask(taskId, {
        status: 'complete',
        progress: 100,
        currentStep: 'Complete',
        completedAt: new Date(),
        output: result,
      });

      toast.success(`Proposal generated for ${contract.title.substring(0, 40)}...`);

    } catch (error) {
      if (signal.aborted) {
        updateTask(taskId, { status: 'cancelled', currentStep: 'Cancelled' });
        return;
      }

      const message = error instanceof Error ? error.message : 'Generation failed';
      updateTask(taskId, {
        status: 'error',
        currentStep: 'Error',
        error: message,
      });
      toast.error(`Generation failed: ${message}`);
    } finally {
      abortControllers.current.delete(taskId);
    }
  };

  // Store proposal to database
  const storeProposal = async (
    contract: Contract,
    result: SourceGroundedProposalOutput,
    profileId: string
  ) => {
    const noticeId = contract.notice_id || contract.id;

    // Delete existing proposal for this contract
    await supabase
      .from('proposal_templates')
      .delete()
      .eq('profile_id', profileId)
      .eq('notice_id', noticeId);

    const templateUrl = `source-grounded-proposals/${noticeId}/${Date.now()}/proposal.pdf`;

    // Normalize all content
    const rawCoverLetter = result.proposal.coverLetter?.contentWithCitations || result.proposal.coverLetter?.content || '';
    const rawExecutiveSummary = result.proposal.executiveSummary?.contentWithCitations || result.proposal.executiveSummary?.content || '';
    const rawTechnicalApproach = result.proposal.technicalApproach?.contentWithCitations || result.proposal.technicalApproach?.content || '';
    const rawManagementApproach = result.proposal.managementApproach?.contentWithCitations || result.proposal.managementApproach?.content || '';
    const rawPastPerformance = result.proposal.pastPerformance?.contentWithCitations || result.proposal.pastPerformance?.content || '';
    const rawPersonnel = result.proposal.personnel?.contentWithCitations || result.proposal.personnel?.content || '';
    const rawPricingStrategy = result.proposal.pricingStrategy?.contentWithCitations || result.proposal.pricingStrategy?.content || '';
    const rawCompliance = result.proposal.compliance?.contentWithCitations || result.proposal.compliance?.content || '';

    const normalizedCoverLetter = normalizeForStorage(rawCoverLetter, 'coverLetter');
    const normalizedExecutiveSummary = normalizeForStorage(rawExecutiveSummary, 'executiveSummary');
    const normalizedTechnicalApproach = normalizeForStorage(rawTechnicalApproach, 'technicalApproach');
    const normalizedManagementApproach = normalizeForStorage(rawManagementApproach, 'managementApproach');
    const normalizedPastPerformance = normalizeForStorage(rawPastPerformance, 'pastPerformance');
    const normalizedPersonnel = normalizeForStorage(rawPersonnel, 'personnel');
    const normalizedPricingStrategy = normalizeForStorage(rawPricingStrategy, 'pricingStrategy');
    const normalizedCompliance = normalizeForStorage(rawCompliance, 'compliance');

    const proposalRecord = {
      profile_id: profileId,
      notice_id: noticeId,
      template_name: `Source-Grounded Proposal: ${contract.title}`,
      template_url: templateUrl,
      content_json: {
        coverLetter: normalizedCoverLetter,
        technicalSections: [
          { title: 'Executive Summary', content: normalizedExecutiveSummary },
          { title: 'Technical Approach', content: normalizedTechnicalApproach },
          { title: 'Management Approach', content: normalizedManagementApproach },
          { title: 'Past Performance', content: normalizedPastPerformance },
          { title: 'Key Personnel', content: normalizedPersonnel },
          { title: 'Pricing & Cost Strategy', content: normalizedPricingStrategy },
          { title: 'FAR/DFARS Compliance', content: normalizedCompliance },
        ],
        feedback: {
          type: 'source-grounded',
          winProbability: result.winAssessment?.winProbabilityEstimate || 0,
          competitivePosition: result.winAssessment?.competitivePosition || 'UNKNOWN',
          strengths: result.winAssessment?.strengthAreas || [],
          weaknesses: result.winAssessment?.weaknessAreas || [],
        },
        executiveSummary: normalizedExecutiveSummary,
        technicalApproach: normalizedTechnicalApproach,
        managementApproach: normalizedManagementApproach,
        pastPerformance: normalizedPastPerformance,
        personnel: normalizedPersonnel,
        pricingStrategy: normalizedPricingStrategy,
        compliance: normalizedCompliance,
        sourceGroundedOutput: result,
        proposalType: 'source-grounded',
        deliverablesData: (result as any).deliverablesData || null,
      },
    };

    const { error } = await supabase.from('proposal_templates').insert(proposalRecord);

    if (error) {
      console.error('Failed to store proposal:', error);
      throw new Error('Failed to save proposal: ' + error.message);
    }
  };

  // Cancel a generation
  const cancelGeneration = useCallback((taskId: string) => {
    const controller = abortControllers.current.get(taskId);
    if (controller) {
      controller.abort();
      updateTask(taskId, { status: 'cancelled', currentStep: 'Cancelled by user' });
    }
  }, [updateTask]);

  // Remove a task from the list
  const removeTask = useCallback((taskId: string) => {
    const controller = abortControllers.current.get(taskId);
    if (controller) {
      controller.abort();
      abortControllers.current.delete(taskId);
    }
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  // Clear all completed/cancelled/error tasks
  const clearCompleted = useCallback(() => {
    setTasks(prev => prev.filter(task =>
      task.status === 'pending' || task.status === 'analyzing' || task.status === 'generating'
    ));
  }, []);

  // Toggle widget expanded state
  const toggleWidgetExpanded = useCallback(() => {
    setIsWidgetExpanded(prev => !prev);
  }, []);

  // Toggle widget minimized state
  const toggleWidgetMinimized = useCallback(() => {
    setIsWidgetMinimized(prev => !prev);
  }, []);

  // Get a specific task
  const getTask = useCallback((taskId: string) => {
    return tasks.find(task => task.id === taskId);
  }, [tasks]);

  const value: BackgroundGenerationContextType = {
    tasks,
    isWidgetExpanded,
    isWidgetMinimized,
    startGeneration,
    cancelGeneration,
    removeTask,
    clearCompleted,
    toggleWidgetExpanded,
    toggleWidgetMinimized,
    getTask,
  };

  return (
    <BackgroundGenerationContext.Provider value={value}>
      {children}
    </BackgroundGenerationContext.Provider>
  );
}
